import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRightIcon, PlusIcon, SparklesIcon, UploadIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { operatorsSkillsQueryOptions } from "../../lib/operatorsSkillsReactQuery";
import { OPERATORS_SKILL_PATH } from "../../operatorsRoutes";
import { OperatorsSubpageShell } from "../operators/OperatorsSubpageShell";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CreateSkillDialog, ImportSkillDialog } from "./SkillDialogs";
import { SkillBadges } from "./SkillBadges";

export function SkillsPage() {
  const navigate = useNavigate();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState<"create" | "import" | null>(null);
  const skillsQuery = useQuery(operatorsSkillsQueryOptions(includeArchived));
  const skills = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const items = skillsQuery.data?.items ?? [];
    return needle
      ? items.filter((skill) => `${skill.name} ${skill.description}`.toLowerCase().includes(needle))
      : items;
  }, [search, skillsQuery.data]);

  const openSkill = (skillId: string) =>
    navigate({ params: { skillId }, to: OPERATORS_SKILL_PATH });

  return (
    <OperatorsSubpageShell
      header={
        <>
          <SparklesIcon className="size-4 text-muted-foreground" />
          <span className="text-sm font-medium">Skills</span>
        </>
      }
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => setDialog("import")}>
            <UploadIcon />
            Import
          </Button>
          <Button size="sm" onClick={() => setDialog("create")}>
            <PlusIcon />
            New skill
          </Button>
        </>
      }
      contentClassName="max-w-4xl space-y-6"
    >
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Skills Library</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Reusable instructions, scripts, and references Codex can use in new threads. Refresh
          skills in a running thread to load saved changes.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          aria-label="Search skills"
          className="w-full sm:max-w-sm"
          placeholder="Search by name or description"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-archived-skills"
            checked={includeArchived}
            onCheckedChange={setIncludeArchived}
          />
          <Label
            htmlFor="show-archived-skills"
            className="cursor-pointer text-sm font-normal text-muted-foreground"
          >
            Show archived
          </Label>
        </div>
      </div>

      {skillsQuery.isPending ? (
        <p role="status" className="py-12 text-center text-sm text-muted-foreground">
          Loading skills…
        </p>
      ) : skillsQuery.isError ? (
        <Card role="alert" className="gap-1 p-6">
          <p className="font-medium text-destructive">Could not load skills</p>
          <p className="text-sm text-muted-foreground">
            {skillsQuery.error instanceof Error
              ? skillsQuery.error.message
              : "Try refreshing the page."}
          </p>
        </Card>
      ) : skills.length === 0 ? (
        <Card className="items-center gap-3 px-6 py-12 text-center">
          <SparklesIcon className="size-8 text-muted-foreground" />
          <div>
            <p className="font-medium">{search ? "No matching skills" : "No skills yet"}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {search
                ? "Try a different name or description."
                : "Create reusable guidance here, or import an existing skill package."}
            </p>
          </div>
          {search ? (
            <Button size="sm" variant="outline" onClick={() => setSearch("")}>
              Clear search
            </Button>
          ) : (
            <Button size="sm" onClick={() => setDialog("create")}>
              <PlusIcon /> Create skill
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {skills.map((skill) => (
            <Card key={skill.id} className="gap-4 p-5 transition-colors hover:bg-accent/20">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="break-words font-mono text-sm font-medium">{skill.name}</h2>
                    <SkillBadges skill={skill} />
                  </div>
                  <p className="line-clamp-2 text-sm text-muted-foreground">{skill.description}</p>
                  <p className="break-all text-xs text-muted-foreground">
                    {skill.ownerEmail ? `${skill.ownerEmail} · ` : ""}Updated{" "}
                    {new Date(skill.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  size="xs"
                  variant="outline"
                  aria-label={`Open ${skill.name}`}
                  onClick={() => void openSkill(skill.id)}
                >
                  Open <ArrowRightIcon />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {dialog === "create" ? <CreateSkillDialog onClose={() => setDialog(null)} /> : null}
      {dialog === "import" ? <ImportSkillDialog onClose={() => setDialog(null)} /> : null}
    </OperatorsSubpageShell>
  );
}
